<template>
  <div class="fixed-points-map-view">
    <b-card class="mb-1">
      <b-row>
        <b-col md="6">
          <h4 class="mb-0">
            خريطة النقاط الثابتة
          </h4>
        </b-col>
        <b-col md="6">
          <b-form-select
            v-model="selectedBasemap"
            :options="basemapOptions"
            size="sm"
            class="mr-1 basemap-select"
            @change="changeBasemap"
          />
        </b-col>
        <b-col
          col
          md="6"
        />
      </b-row>

    </b-card>

    <div class="map-container-wrapper mb-1">
      <div
        v-if="mapLoading"
        class="loading-overlay"
      >
        <div class="spinner" />
        <p class="text-white mt-2">
          جاري تحميل الخريطة...
        </p>
      </div>
      <div
        ref="esriView"
        class="map-view"
      />
      <!-- Refresh Map Button - positioned under zoom controls -->
      <button
        class="map-refresh-button"
        :disabled="mapLoading || fixedPointsLoading"
        :title="'تحديث الخريطة'"
        @click="refreshMap"
      >
        <b-spinner
          v-if="mapLoading || fixedPointsLoading"
          small
        />
        <feather-icon
          v-else
          icon="RefreshCwIcon"
          size="16"
        />
      </button>
    </div>

    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-1">
        <h5 class="mb-0">
          النقاط الثابتة على الخريطة
        </h5>
        <b-spinner
          v-if="fixedPointsLoading"
          small
        />
      </div>
      <b-alert
        v-if="!fixedPointsLoading && totalPoints === 0"
        variant="info"
        show
      >
        لا توجد نقاط ثابتة للعرض على الخريطة.
      </b-alert>
      <div
        v-else-if="!fixedPointsLoading && totalPoints > 0"
        class="mt-2"
      >
        <small class="text-muted">
          انقر على أي نقطة على الخريطة لعرض التفاصيل
        </small>
      </div>
    </b-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { loadArcgisModules } from '@/utils/arcgisLoader'
import {
  BCard,
  BButton,
  BSpinner,
  BAlert,
  BBadge,
  BFormSelect,
  BRow,
  BCol,
} from 'bootstrap-vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'

export default {
  name: 'FixedPointsMap',
  components: {
    BCard,
    BButton,
    BSpinner,
    BAlert,
    BBadge,
    BFormSelect,
    BRow,
    BCol,
    FeatherIcon,
  },
  data() {
    return {
      map: null,
      view: null,
      fixedPointsLayer: null,
      userLocationLayer: null,
      arcgisModules: null,
      mapLoading: false,
      userLocation: null, // Store user's current location { lat, lng }
      selectedBasemap: 'hybrid',
      basemapOptions: [
        { value: 'satellite', text: 'الخرائط الفضائية' },
        { value: 'streets', text: 'الخرائط العادية' },
        { value: 'hybrid', text: 'مختلط' },
        // { value: 'streets-navigation-vector', text: 'الخرائط العادية مع مسارات المشاة والسيارات' },
        { value: 'topo', text: 'طبوغرافي' },
        { value: 'gray', text: 'رمادي' },
        { value: 'dark-gray', text: 'رمادي داكن' },
        { value: 'oceans', text: 'المحيطات' },
      ],
    }
  },
  computed: {
    ...mapGetters({
      fixedPoints: 'fixedPoints/fixedPoints',
      fixedPointsLoading: 'fixedPoints/load',
    }),
    totalPoints() {
      const fixedPointsData = this.fixedPoints || []
      return fixedPointsData.length
    },
  },
  watch: {
    fixedPoints: {
      handler(newVal) {
        console.log('FixedPoints watcher triggered, data:', newVal)
        if (this.map && this.arcgisModules && newVal) {
          this.$nextTick(() => {
            this.renderFixedPoints()
          })
        }
      },
      deep: true,
      immediate: false,
    },
  },
  mounted() {
    this.initMap()
  },
  beforeDestroy() {
    if (this.view) {
      this.view.destroy()
    }
    if (this.map) {
      this.map.destroy()
    }
  },
  methods: {
    async initMap() {
      this.mapLoading = true
      try {
        const [
          Map,
          MapView,
          GraphicsLayer,
          Basemap,
          Point,
          Graphic,
          PictureMarkerSymbol,
          Extent,
        ] = await loadArcgisModules([
          'esri/Map',
          'esri/views/MapView',
          'esri/layers/GraphicsLayer',
          'esri/Basemap',
          'esri/geometry/Point',
          'esri/Graphic',
          'esri/symbols/PictureMarkerSymbol',
          'esri/geometry/Extent',
        ])

        this.arcgisModules = {
          Map,
          MapView,
          GraphicsLayer,
          Basemap,
          Point,
          Graphic,
          PictureMarkerSymbol,
          Extent,
        }

        // Create fixed points layer
        this.fixedPointsLayer = new GraphicsLayer()

        // Create user location layer
        this.userLocationLayer = new GraphicsLayer()

        // Create map
        this.map = new Map({
          basemap: this.selectedBasemap,
          layers: [this.fixedPointsLayer, this.userLocationLayer],
        })

        // Create view with default center (will be updated if user location is available)
        this.view = new MapView({
          container: this.$refs.esriView,
          map: this.map,
          center: [31.2357, 30.0444], // [longitude, latitude] - Cairo, Egypt (default)
          zoom: 10,
        })

        // Wait for view to be ready
        await this.view.when()
        console.log('Map initialized with fixed points layer')

        // Try to get user's current location
        await this.getUserLocation()

        // Fetch fixed points (render will be triggered by watcher)
        await this.fetchFixedPoints()

        // Also manually trigger render after a short delay to ensure data is loaded
        setTimeout(() => {
          this.renderFixedPoints()
        }, 500)

        this.mapLoading = false
      } catch (error) {
        console.error('Error initializing map:', error)
        this.mapLoading = false
        this.$toast.error('حدث خطأ أثناء تحميل الخريطة')
      }
    },
    async fetchFixedPoints() {
      try {
        const response = await this.$store.dispatch('fixedPoints/listFixedPoints', {
          query: {
            per_page: 1000, // Get all fixed points for the map
          },
        })
        console.log('Fixed points fetched, response:', response)
        console.log('Store fixedPoints after fetch:', this.fixedPoints)
        // Render will be triggered by watcher, but also manually trigger after a delay
        this.$nextTick(() => {
          setTimeout(() => {
            this.renderFixedPoints()
          }, 300)
        })
      } catch (error) {
        console.error('Error fetching fixed points:', error)
        this.$toast.error('حدث خطأ أثناء جلب النقاط الثابتة')
      }
    },
    createPinIcon(colorHex) {
      // Create an SVG pin/marker icon with the specified color
      // The pin point is at the bottom center (coordinates location)
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
          <!-- Pin shadow -->
          <ellipse cx="16" cy="36" rx="4" ry="2" fill="rgba(0,0,0,0.2)"/>
          <!-- Pin body -->
          <path d="M16 2C10.477 2 6 6.477 6 12c0 6 10 24 10 24s10-18 10-24C26 6.477 21.523 2 16 2z" 
                fill="${colorHex}" 
                stroke="#ffffff" 
                stroke-width="2"/>
          <!-- Pin center circle -->
          <circle cx="16" cy="12" r="5" fill="#ffffff"/>
          <circle cx="16" cy="12" r="3" fill="${colorHex}"/>
        </svg>
      `.trim()
      return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`
    },
    createUserLocationIcon() {
      // Create an SVG icon for user's current location (blue circle with pulsing effect)
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
          <!-- Outer pulsing circle -->
          <circle cx="24" cy="24" r="20" fill="#0066ff" opacity="0.3">
            <animate attributeName="r" values="20;24;20" dur="2s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.3;0.1;0.3" dur="2s" repeatCount="indefinite"/>
          </circle>
          <!-- Middle circle -->
          <circle cx="24" cy="24" r="14" fill="#0066ff" opacity="0.5"/>
          <!-- Inner circle -->
          <circle cx="24" cy="24" r="8" fill="#0066ff" stroke="#ffffff" stroke-width="2"/>
          <!-- Center dot -->
          <circle cx="24" cy="24" r="4" fill="#ffffff"/>
        </svg>
      `.trim()
      return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`
    },
    addUserLocationMarker(lat, lng) {
      if (!this.userLocationLayer || !this.arcgisModules) {
        console.log('User location layer or modules not ready')
        return
      }

      const { Point, Graphic, PictureMarkerSymbol } = this.arcgisModules

      // Clear any existing user location marker
      this.userLocationLayer.removeAll()

      // Create point for user location
      const userPoint = new Point({
        longitude: lng,
        latitude: lat,
        spatialReference: { wkid: 4326 },
      })

      // Create user location icon
      const userIconSvg = this.createUserLocationIcon()

      const userSymbol = new PictureMarkerSymbol({
        url: userIconSvg,
        width: 48,
        height: 48,
        xoffset: 0,
        yoffset: 0,
      })

      // Create graphic for user location
      const userGraphic = new Graphic({
        geometry: userPoint,
        symbol: userSymbol,
        attributes: {
          type: 'user_location',
          latitude: lat,
          longitude: lng,
        },
        popupTemplate: {
          title: 'موقعك الحالي',
          content: `
            <div style="padding: 8px;">
              <p><strong>خط العرض:</strong> ${lat.toFixed(6)}</p>
              <p><strong>خط الطول:</strong> ${lng.toFixed(6)}</p>
            </div>
          `,
        },
      })

      // Add user location marker to the layer
      this.userLocationLayer.add(userGraphic)
      console.log('User location marker added at:', lat, lng)
    },
    buildFixedPointTooltip(fixedPoint) {
      let content = '<div style="padding: 8px;">'
      content += `<p><strong>الكود:</strong> ${fixedPoint.code || '-'}</p>`

      if (fixedPoint.level) {
        content += `<p><strong>المستوى:</strong> ${fixedPoint.level}</p>`
      }

      if (fixedPoint.accuracy_degree_label_ar) {
        content += `<p><strong>درجة الدقة:</strong> ${fixedPoint.accuracy_degree_label_ar}</p>`
      }

      if (fixedPoint.responsible_employee) {
        content += `<p><strong>الموظف المسؤول:</strong> ${fixedPoint.responsible_employee.full_name || '-'}</p>`
      }
      content += `<p><strong>نوع التصحيح:</strong> ${fixedPoint.correction_type === 'after' ? 'بعد التصحيح' : 'قبل التصحيح'}</p>`

      // Show coordinates based on coord_type
      if (fixedPoint.coord_type === 'lat_lon' && fixedPoint.latitude && fixedPoint.longitude) {
        content += `<p><strong>خط العرض:</strong> ${fixedPoint.latitude}</p>`
        content += `<p><strong>خط الطول:</strong> ${fixedPoint.longitude}</p>`
      } else if (fixedPoint.coord_type === 'etm' && fixedPoint.etm_est && fixedPoint.etm_north) {
        content += `<p><strong>ETM East:</strong> ${fixedPoint.etm_est}</p>`
        content += `<p><strong>ETM North:</strong> ${fixedPoint.etm_north}</p>`
        if (fixedPoint.etm_zone) {
          content += `<p><strong>ETM Zone:</strong> ${fixedPoint.etm_zone}</p>`
        }
      } else if (fixedPoint.coord_type === 'utm' && fixedPoint.utm_est && fixedPoint.utm_north) {
        content += `<p><strong>UTM East:</strong> ${fixedPoint.utm_est}</p>`
        content += `<p><strong>UTM North:</strong> ${fixedPoint.utm_north}</p>`
        if (fixedPoint.utm_zone) {
          content += `<p><strong>UTM Zone:</strong> ${fixedPoint.utm_zone}</p>`
        }
      }

      // Add link button to view fixed point details
      if (fixedPoint.id) {
        const route = this.$router.resolve({
          name: 'fixed-point-details',
          params: { id: fixedPoint.id },
        })
        const fixedPointUrl = route.href.startsWith('/')
          ? `${window.location.origin}${route.href}`
          : route.href
        content += '<div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #e0e0e0;">'
        content += `<a href="${fixedPointUrl}" target="_blank" style="display: inline-block; padding: 8px 16px; background-color: #06284D; color: white; text-decoration: none; border-radius: 4px; font-size: 14px; font-weight: 500;">عرض التفاصيل</a>`
        content += '</div>'
      }

      content += '</div>'
      return content
    },
    renderFixedPoints() {
      if (!this.fixedPointsLayer || !this.arcgisModules) {
        console.log('Fixed points layer or modules not ready')
        return
      }

      const { Point, Graphic, PictureMarkerSymbol } = this.arcgisModules
      this.fixedPointsLayer.removeAll()

      // Handle both array and object with data property
      let fixedPointsData = []
      if (Array.isArray(this.fixedPoints)) {
        fixedPointsData = this.fixedPoints
      } else if (this.fixedPoints?.data && Array.isArray(this.fixedPoints.data)) {
        fixedPointsData = this.fixedPoints.data
      } else if (this.fixedPoints?.data?.data && Array.isArray(this.fixedPoints.data.data)) {
        fixedPointsData = this.fixedPoints.data.data
      }

      console.log('Fixed points from store:', this.fixedPoints)
      console.log('Fixed points data to render:', fixedPointsData)
      console.log('Fixed points count:', fixedPointsData.length)

      if (!fixedPointsData.length) {
        console.log('No fixed points data to render')
        return
      }

      let renderedCount = 0
      const validPoints = []

      fixedPointsData.forEach(fixedPoint => {
        // Only render points with latitude/longitude (for now)
        // TODO: Convert ETM/UTM to lat/lon if needed
        let lat; let
          lng

        // Check coord_type first, but also fallback to checking if lat/lng exist
        // if (fixedPoint.coord_type === 'lat_lon' && fixedPoint.latitude && fixedPoint.longitude) {
        lat = parseFloat(fixedPoint.latitude)
        lng = parseFloat(fixedPoint.longitude)
        // } else if (!fixedPoint.coord_type && fixedPoint.latitude && fixedPoint.longitude) {
        //   // Fallback: if no coord_type but has lat/lng, use them
        //   lat = parseFloat(fixedPoint.latitude);
        //   lng = parseFloat(fixedPoint.longitude);
        // } else {
        //   // Skip ETM/UTM for now - would need conversion library
        //   console.log('Skipping fixed point (not lat/lon or no conversion):', fixedPoint.code || fixedPoint.id, 'coord_type:', fixedPoint.coord_type);
        //   return;
        // }

        if (!lat || !lng || isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) {
          console.log('Skipping fixed point (invalid coordinates):', fixedPoint.code || fixedPoint.id, 'lat:', lat, 'lng:', lng)
          return
        }

        // Validate coordinate ranges
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
          console.log('Skipping fixed point (coordinates out of range):', fixedPoint.code || fixedPoint.id, 'lat:', lat, 'lng:', lng)
          return
        }

        // Create point with longitude, latitude
        // ArcGIS Point uses longitude, latitude order and WGS84 (4326) spatial reference
        const point = new Point({
          longitude: lng,
          latitude: lat,
          spatialReference: { wkid: 4326 }, // WGS84
        })

        // Get color from accuracy_degree_color or default
        const colorHex = fixedPoint.accuracy_degree_color || '#0099ff'

        // Create pin-style marker
        const pinSvg = this.createPinIcon(colorHex)

        const symbol = new PictureMarkerSymbol({
          url: pinSvg,
          width: 32,
          height: 40,
          xoffset: 0,
          yoffset: 20, // Offset to position the pin point at the coordinates
        })

        // Build tooltip content
        const tooltipContent = this.buildFixedPointTooltip(fixedPoint)

        const graphic = new Graphic({
          geometry: point,
          symbol,
          attributes: {
            id: fixedPoint.id,
            code: fixedPoint.code,
            latitude: lat,
            longitude: lng,
            level: fixedPoint.level,
            accuracy_degree: fixedPoint.accuracy_degree_label_ar || fixedPoint.accuracy_degree,
            accuracy_degree_color: fixedPoint.accuracy_degree_color,
            responsible_employee: fixedPoint.responsible_employee
              ? fixedPoint.responsible_employee.full_name
              : '-',
            coord_type: fixedPoint.coord_type,
            etm_est: fixedPoint.etm_est,
            etm_north: fixedPoint.etm_north,
            etm_zone: fixedPoint.etm_zone,
            utm_est: fixedPoint.utm_est,
            utm_north: fixedPoint.utm_north,
            utm_zone: fixedPoint.utm_zone,
            created_at: fixedPoint.created_at,
          },
          popupTemplate: {
            title: fixedPoint.code || 'نقطة ثابتة',
            content: tooltipContent,
          },
        })

        this.fixedPointsLayer.add(graphic)
        validPoints.push({ lat, lng })
        renderedCount++
      })

      console.log(`Rendered ${renderedCount} fixed point markers on the map`)

      // Only fit view if user location is not available
      // If user location exists, keep the map centered on it
      if (this.view && !this.userLocation) {
        this.fitViewToAllPoints(validPoints)
      }
    },
    fitViewToAllPoints(fixedPoints) {
      if (!this.view || !this.arcgisModules) {
        console.log('View or modules not available')
        return
      }

      try {
        const { Extent } = this.arcgisModules
        if (!Extent) {
          console.error('Extent module not loaded')
          return
        }

        // Collect all points: fixed points + user location if available
        const allPoints = [...fixedPoints]

        // Add user location if available
        if (this.userLocation && this.userLocation.lat && this.userLocation.lng) {
          allPoints.push({
            lat: this.userLocation.lat,
            lng: this.userLocation.lng,
          })
        }

        if (allPoints.length === 0) {
          console.log('No valid coordinates to fit view')
          return
        }

        // Calculate bounding box for all points
        const lngs = allPoints.map(p => p.lng)
        const lats = allPoints.map(p => p.lat)
        const minLng = Math.min(...lngs)
        const maxLng = Math.max(...lngs)
        const minLat = Math.min(...lats)
        const maxLat = Math.max(...lats)

        // Add padding (15% of the range for better visibility)
        const lngRange = maxLng - minLng
        const latRange = maxLat - minLat
        const lngPadding = lngRange > 0 ? lngRange * 0.15 : 0.02
        const latPadding = latRange > 0 ? latRange * 0.15 : 0.02

        const extent = new Extent({
          xmin: minLng - lngPadding,
          ymin: minLat - latPadding,
          xmax: maxLng + lngPadding,
          ymax: maxLat + latPadding,
          spatialReference: { wkid: 4326 },
        })

        this.view.goTo(extent).catch(err => {
          console.error('Error fitting view to extent:', err)
        })
      } catch (error) {
        console.error('Error in fitViewToAllPoints:', error)
      }
    },
    refreshMap() {
      this.fetchFixedPoints()
    },
    changeBasemap() {
      if (this.map && this.selectedBasemap) {
        try {
          this.map.basemap = this.selectedBasemap
        } catch (error) {
          console.error('Error changing basemap:', error)
          this.$toast.error('تعذر تغيير نوع الخريطة')
        }
      }
    },
    getUserLocation() {
      return new Promise(resolve => {
        // Check if geolocation is supported
        if (!navigator.geolocation) {
          console.log('Geolocation is not supported by this browser')
          this.$toast.info('المتصفح لا يدعم تحديد الموقع الجغرافي')
          resolve()
          return
        }

        // Request user's location
        navigator.geolocation.getCurrentPosition(
          position => {
            // Success: Got user's location
            const lat = position.coords.latitude
            const lng = position.coords.longitude
            console.log('User location:', lat, lng)

            // Store user location
            this.userLocation = { lat, lng }

            // Add user location marker
            this.addUserLocationMarker(lat, lng)

            // Center and zoom map on user's location
            if (this.view) {
              this.view.goTo({
                center: [lng, lat],
                zoom: 10, // Zoom level 15 for better detail
              }).then(() => {
                this.$toast.success('تم تحديد موقعك بنجاح')
                resolve()
              }).catch(err => {
                console.error('Error centering map on user location:', err)
                resolve()
              })
            } else {
              resolve()
            }
          },
          error => {
            // Error: Handle different error cases
            let errorMessage = ''
            switch (error.code) {
              case error.PERMISSION_DENIED:
                errorMessage = 'تم رفض طلب الوصول إلى موقعك. يرجى السماح بالوصول إلى الموقع في إعدادات المتصفح.'
                this.$swal({
                  title: 'طلب الوصول إلى الموقع',
                  text: 'نحتاج إلى إذنك للوصول إلى موقعك الجغرافي لعرض الخريطة بشكل أفضل. يرجى السماح بالوصول في إعدادات المتصفح.',
                  icon: 'info',
                  confirmButtonText: 'حسناً',
                  confirmButtonColor: '#06284D',
                })
                break
              case error.POSITION_UNAVAILABLE:
                errorMessage = 'معلومات الموقع غير متاحة.'
                this.$toast.warning('معلومات الموقع غير متاحة')
                break
              case error.TIMEOUT:
                errorMessage = 'انتهت مهلة طلب الموقع.'
                this.$toast.warning('انتهت مهلة طلب الموقع')
                break
              default:
                errorMessage = 'حدث خطأ غير معروف أثناء الحصول على الموقع.'
                this.$toast.warning('حدث خطأ أثناء الحصول على الموقع')
                break
            }
            console.error('Geolocation error:', errorMessage, error)
            resolve()
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          },
        )
      })
    },
  },
}
</script>

<style scoped>
.fixed-points-map-view {
  height: 100%;
}

.map-container-wrapper {
  position: relative;
  height: 600px;
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.map-view {
  width: 100%;
  height: 100%;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.map-refresh-button {
  position: absolute;
  top: 80px; /* Position below zoom controls (zoom controls are typically ~50px tall) */
  right: 10px;
  width: 40px;
  height: 40px;
  background-color: #ffffff;
  border: 1px solid #cccccc;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 100;
  transition: background-color 0.2s;
}

.map-refresh-button:hover:not(:disabled) {
  background-color: #f5f5f5;
}

.map-refresh-button:active:not(:disabled) {
  background-color: #e0e0e0;
}

.map-refresh-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.map-refresh-button svg {
  color: #333333;
}
</style>
